import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Textarea,
    ModalFooter,
    Button,
    Text,
    Box,
    Image,
    Checkbox,
    Stack,
    Select,
} from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { useCreateRewardMutation, useUpdateRewardMutation } from "../../slices/adminApiSlice";
import { ToastService } from '../../services/toast/toastService';

const AdminAddReward = ({ selectedReward, isOpen, onClose, fetchRewards = () => {} }) => {
    const [createReward] = useCreateRewardMutation();
    const [updateReward, { isLoading: isUpdating }] = useUpdateRewardMutation();
    const { success } = ToastService();
    const [formErrors, setFormErrors] = useState({});
    const [keepCurrentImage, setKeepCurrentImage] = useState(false);
    const [formValues, setFormValues] = useState({
        type: '',
        title: '',
        rewardImage: null,
        description: '',
        points_required: 0,
        months_to_expire: 0,
    });

    useEffect(() => {
        if (selectedReward) {
            setFormValues({
                type: selectedReward.type,
                title: selectedReward.title,
                rewardImage: null,
                description: selectedReward.description,
                points_required: selectedReward.points_required,
                months_to_expire: selectedReward.months_to_expire,
            });
            setKeepCurrentImage(true);
        } else {
            resetForm();
        }
    }, [selectedReward]);

    const resetForm = () => {
        setFormValues({
            type: '',
            title: '',
            rewardImage: null,
            description: '',
            points_required: 0,
            months_to_expire: 0,
        });
        setFormErrors({});
        setKeepCurrentImage(false);
    };

    const validateForm = () => {
        const errors = {};
        if (!formValues.title) errors.title = 'Title is required.';
        if (!keepCurrentImage && !formValues.rewardImage) {
            errors.rewardImage = 'Reward image is required.';
        }
        if (!formValues.description) errors.description = 'Description is required.';
        if (formValues.points_required <= 0) errors.points_required = 'Points required must be greater than 0.';
        if (formValues.months_to_expire <= 0) errors.months_to_expire = 'Months to expire must be greater than 0.';
        return errors;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const formData = new FormData();
        Object.keys(formValues).forEach((key) => {
            if (key !== 'rewardImage' || !keepCurrentImage) {
                formData.append(key, formValues[key]);
            }
        });

        try {
            if (selectedReward) {
                const response = await updateReward({ id: selectedReward._id, data: formData }).unwrap();
                success(response.message || "Reward updated successfully!");
            } else {
                await createReward(formData).unwrap();
                success('Reward created successfully.');
            }
            fetchRewards();
            resetForm();
            onClose();
        } catch (error) {
            // console.error('Error submitting reward:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, type } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: type !== 'file' ? e.target.value : prevValues[name],
        }));
    };    

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormValues((prevValues) => ({
                ...prevValues,
                rewardImage: file
            }));
            setKeepCurrentImage(false);
        }
    };
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{selectedReward ? 'Edit Reward' : 'Create Reward'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <form onSubmit={handleFormSubmit}>
                        <FormControl id="type" isRequired>
                            <FormLabel>Reward Type</FormLabel>
                            <Select
                                value={formValues.type || "point"} // Default value set to "point"
                                onChange={(e) => setFormValues((prev) => ({ ...prev, type: e.target.value }))}
                            >
                                <option value="point" selected>Point</option>
                                <option value="stamp">Stamp</option>
                            </Select>
                        </FormControl>
                        
                        <FormControl isRequired isInvalid={!!formErrors.title}>
                            <FormLabel>Title</FormLabel>
                            <Input
                                type="text"
                                name="title"
                                value={formValues.title}
                                onChange={handleInputChange}
                                isDisabled={isUpdating}
                            />
                            <FormErrorMessage>{formErrors.title}</FormErrorMessage>
                        </FormControl>

                        <FormControl mt={2} isInvalid={!!formErrors.rewardImage}>
                            <FormLabel>Image</FormLabel>
                            <Input
                                type="file"
                                name="rewardImage"
                                accept="image/*"
                                onChange={handleImageChange}
                                isDisabled={isUpdating}
                            />
                            <FormErrorMessage>
                                {formErrors.rewardImage && !keepCurrentImage ? formErrors.rewardImage : null}
                            </FormErrorMessage>
                            
                            {selectedReward && selectedReward.image_url ? (
                                <Box mt={2}>
                                    <Text fontSize="sm" color="gray.500">
                                        Current Image:
                                    </Text>
                                    <Image 
                                        src={ 
                                            selectedReward.image_url
                                            ? selectedReward.image_url 
                                            : '/default_reward.jpg'
                                        }
                                        alt="Current Reward" 
                                        boxSize="100px" 
                                        objectFit="cover" 
                                        borderRadius="md" 
                                        mt={1}
                                    />
                                    <Checkbox 
                                        mt={1} 
                                        isChecked={keepCurrentImage} 
                                        onChange={(e) => setKeepCurrentImage(e.target.checked)}
                                    >
                                        Keep current image
                                    </Checkbox>
                                </Box>
                            ) : (
                                <Text mt={1} fontSize="sm" color="gray.500">
                                    No current image set. Please upload a new image.
                                </Text>
                            )}

                            {formValues.rewardImage && (
                                <Text mt={1} fontSize="sm" color="green.500">
                                    New file chosen: {formValues.rewardImage.name}
                                </Text>
                            )}
                        </FormControl>

                        <FormControl mt={2} isRequired isInvalid={!!formErrors.description}>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                name="description"
                                value={formValues.description}
                                onChange={handleInputChange}
                                isDisabled={isUpdating}
                            />
                            <FormErrorMessage>{formErrors.description}</FormErrorMessage>
                        </FormControl>

                        <FormControl mt={2} isRequired isInvalid={!!formErrors.points_required}>
                            <FormLabel>Points Required</FormLabel>
                            <Input
                                type="number"
                                name="points_required"
                                value={formValues.points_required}
                                onChange={handleInputChange}
                                isDisabled={isUpdating}
                            />
                            <FormErrorMessage>{formErrors.points_required}</FormErrorMessage>
                        </FormControl>

                        <FormControl mt={2} isRequired isInvalid={!!formErrors.months_to_expire}>
                            <FormLabel>Months To Expire</FormLabel>
                            <Input
                                type="number"
                                name="months_to_expire"
                                value={formValues.months_to_expire}
                                onChange={handleInputChange}
                                isDisabled={isUpdating}
                            />
                            <FormErrorMessage>{formErrors.months_to_expire}</FormErrorMessage>
                        </FormControl>

                        <ModalFooter>
                        <Stack spacing={4} direction={['column', 'row']}>
                            <Button
                                colorScheme='teal'
                                variant={'outline'}
                                width="full" 
                                mt={4}
                                _hover={{ bg: 'teal.500', color: 'white' }} 
                                type="submit" 
                                isLoading={isUpdating} 
                                disabled={isUpdating}>
                                {selectedReward ? 'Update' : 'Save'}
                            </Button>
                            <Button
                                colorScheme='red'
                                variant={'outline'}
                                width="full" 
                                mt={4}
                                _hover={{ bg: 'red.500', color: 'white' }} 
                                onClick={onClose} 
                                isDisabled={isUpdating}>
                                Cancel
                            </Button>
                            </Stack>
                        </ModalFooter>
                    </form>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AdminAddReward;